'use client';

import { type ReactNode, useEffect } from 'react';

import Image from 'next/image';
import Link from 'next/link';

import type { RafflesResponse } from '@/actions';
import type { RaffleOffersResponse } from '@/actions/raffleOffers';
import {
  Badge,
  BottomFixed,
  Button,
  CommunicationBanner,
  Container,
  CountdownTimer,
  Icon,
  LastWinnersCarousel,
  PromotionPacks,
  QuantitySelector,
  RafflesList,
} from '@/components';
import { MustangArrow } from '@/components/ui/MustangArrow';
import { usePromotionOffers, type PromotionOffersState } from '@/hooks';
import { cn, toast } from '@/libs';
import type { CustomerWinnersFeedResponse } from '@/types/models';
import type { CommunicationBanner as CommunicationBannerData } from '@/types/models/CommunicationBanner';
import {
  formatCurrency,
  getRaffleCtaLabel,
  isRaffleOnSale,
  isRaffleUpcoming,
  isWithinDays,
  normalizePackOffers,
  type PromotionOffer,
  UPCOMING_SALE_BADGE_TEXT,
} from '@/utils';

import { FinishedResultsSection } from './FinishedResultsSection';
import { useMainContentWithCheckout } from './hooks';

const OFFERS_FETCH_ERROR_MESSAGE =
  'Não foi possível atualizar as ofertas deste sorteio agora.';

type MainContentProps = {
  communicationBanner: CommunicationBannerData | null;
  highlightedRaffle: RafflesResponse['highlighted'][0] | null;
  customerWinners: CustomerWinnersFeedResponse | null;
  sortedRaffles: RafflesResponse['allRaffles'] | null;
  footer?: ReactNode;
  finishedRaffles: RafflesResponse['finishedRaffles'] | null;
  initialOffers?: RaffleOffersResponse;
};

function derivePackOffers({
  offersState,
  legacyPacks,
}: {
  offersState: PromotionOffersState;
  legacyPacks: unknown;
}): PromotionOffer[] {
  if (offersState.status === 'unified') {
    return normalizePackOffers({
      promotionOffers: offersState.offers,
      legacyPacks: undefined,
    });
  }

  if (offersState.status === 'legacy') {
    return normalizePackOffers({ promotionOffers: undefined, legacyPacks });
  }

  return [];
}

export function MainContent({
  communicationBanner,
  highlightedRaffle: raffle,
  footer,
  customerWinners,
  sortedRaffles,
  finishedRaffles,
  initialOffers,
}: MainContentProps) {
  const offersState = usePromotionOffers({
    raffleId: raffle?.id,
    initialData: initialOffers,
  });
  const packOffers = derivePackOffers({
    offersState,
    legacyPacks: raffle?.promotionPacks,
  });
  const promotionOffers =
    offersState.status === 'unified' ? offersState.offers : undefined;
  const promotionOffersResolved =
    offersState.status === 'unified' || offersState.status === 'legacy';

  useEffect(() => {
    if (offersState.status !== 'error') return;

    toast.error(OFFERS_FETCH_ERROR_MESSAGE);
  }, [offersState.status]);

  const {
    ticketQuantity,
    totalValue,
    addQuantity,
    handleComboClick,
    handleChangeQuantity,
    goToCheckout,
    hasQuantityError,
  } = useMainContentWithCheckout({
    raffle: raffle ?? undefined,
    packOffers,
    promotionOffers,
    promotionOffersResolved,
  });

  const winnersData = customerWinners?.data ?? [];
  const shouldShowLastWinners = !!customerWinners && winnersData.length > 0;
  const banner = <CommunicationBanner banner={communicationBanner} className="mb-4" />;

  if (!raffle) {
    if (finishedRaffles?.length) {
      return (
        <Container>
          {banner}
          <div className="py-8 space-y-4">
            <h2 className="text-2xl font-bold text-foreground">Sorteios Finalizados</h2>
            <RafflesList raffles={finishedRaffles} />
          </div>
        </Container>
      );
    }

    return (
      <Container>
        {banner}
        <div className="text-center py-12">
          <p>Nenhum sorteio disponível no momento.</p>
        </div>
      </Container>
    );
  }

  const canBuy = isRaffleOnSale(raffle.status);
  const isUpcoming = isRaffleUpcoming(raffle.status);

  return (
    <>
      <Container>
        {banner}
        <div
          data-cy="main-content"
          className="grid lg:grid-cols-2 gap-4 md:gap-2 lg:p-4 lg:gap-2 lg:rounded-lg lg:bg-background-variant"
        >
          <div className="flex flex-col relative">
            <Link
              href={`/detalhes/${raffle.id}`}
              data-cy="main-content-raffle-banner-link"
            >
              <Image
                src={raffle.urlBanner || ''}
                alt="banner principal"
                width={1080}
                priority
                height={400}
                unoptimized
                className="object-cover rounded-lg md:w-full md:h-[400px] lg:h-auto"
              />
            </Link>
            {isUpcoming && (
              <Badge
                data-cy="main-content-upcoming-badge"
                text={UPCOMING_SALE_BADGE_TEXT}
                className="absolute top-3 left-3 w-auto gap-1 px-2 py-1 bg-secondary shadow-xl"
                cnText="text-secondary-foreground"
                iconProps={{ width: 14, height: 14 }}
              />
            )}
          </div>

          <div className="flex flex-col gap-4 lg:justify-between">
            <div className="rounded-lg space-y-4">
              <Link
                href={`/detalhes/${raffle.id}`}
                data-cy="main-content-raffle-details-link"
              >
                <div className="flex flex-row items-center justify-between w-full rounded-lg bg-background-variant lg:bg-background p-3">
                  <div className="flex flex-col items-start">
                    <span className="text-foreground uppercase font-semibold md:text-lg">
                      {raffle.name}
                    </span>
                  </div>
                  {raffle.ticketPrice && (
                    <div className="flex flex-col justify-start">
                      <span className="text-foreground/90 text-right text-xs">
                        adquira por
                      </span>
                      <span className="uppercase font-semibold text-xl">
                        {formatCurrency(raffle.ticketPrice)}
                      </span>
                    </div>
                  )}
                </div>
              </Link>

              {!isUpcoming &&
                isWithinDays(new Date(raffle.drawDate || new Date()), 2) && (
                  <div
                    data-cy="main-content-countdown"
                    className="flex flex-row items-center justify-center w-full rounded-lg bg-background-variant lg:bg-background p-2.5"
                  >
                    <div className="flex flex-col items-center justify-start">
                      <CountdownTimer
                        targetDate={new Date(raffle.salesEndDate || new Date())}
                        label="vendas se encerram em"
                        expiredTitle="vendas encerradas"
                        expiredSubtext="aguardando sorteio"
                      />
                    </div>
                  </div>
                )}
            </div>

            {/* Mobile Bottom Fixed */}
            <div className="lg:hidden" data-cy="main-content-mobile-purchase">
              <BottomFixed className="p-3 !gap-0.5 rounded-b-none w-full max-w-screen-xl mx-auto md:p-6 md:inset-x-0 md:bottom-0 shadow-up-md">
                {!isUpcoming && (
                  <>
                    {packOffers.length > 0 && (
                      <PromotionPacks
                        offers={packOffers}
                        ticketPrice={raffle.ticketPrice || 0}
                        onSelectPack={handleComboClick}
                      />
                    )}
                    <BottomFixed.Header className="text-foreground flex flex-row justify-between items-start gap-1 md:gap-2 w-full">
                      {raffle.addTicketQuantityButtonValue &&
                        raffle.addTicketQuantityButtonValue.some((value) => value > 0) &&
                        raffle.addTicketQuantityButtonValue.map((value: number) => (
                          <div className="flex-1 relative" key={value}>
                            {raffle.addTicketQuantityButtonValue[2] === value && (
                              <Badge
                                icon="Fire"
                                className="w-fit p-0.5 absolute -top-2 left-1/2 -translate-x-1/2 shadow-xl"
                                text="popular"
                                cnText="text-xxs font-medium"
                                iconProps={{ width: 12, height: 12 }}
                              />
                            )}
                            <Button
                              data-cy={`main-content-add-quantity-${value}-button`}
                              variant="secondary"
                              onClick={() => addQuantity(value)}
                              className="w-full h-12 text-xl rounded-sm border-2 border-foreground/10 bg-foreground/10 px-6 py-3.5"
                            >
                              {`+${value}`}
                            </Button>
                          </div>
                        ))}
                    </BottomFixed.Header>

                    <BottomFixed.Body className="flex flex-col rounded-lg gap-2 overflow-visible">
                      <div className="flex flex-row items-center justify-between w-full">
                        <div className="flex flex-1 flex-col items-start gap-0 md:gap-1">
                          <span className="text-foreground text-sm uppercase font-medium">
                            total
                          </span>
                          <span
                            data-cy="main-content-total-value"
                            className="text-secondary uppercase font-semibold text-2xl md:text-2xl leading-none"
                          >
                            {formatCurrency(totalValue)}
                          </span>
                        </div>

                        <div className="relative z-10 pointer-events-auto">
                          <QuantitySelector
                            count={ticketQuantity}
                            onSetCount={handleChangeQuantity}
                            min={raffle.minTicketsPerPurchase}
                            max={raffle.maxTicketsPerPurchase}
                            incrementStep={raffle.addTicketButtonStepIncrement}
                            decrementStep={raffle.addTicketButtonStepDecrement}
                          />
                        </div>
                      </div>
                    </BottomFixed.Body>
                  </>
                )}

                <BottomFixed.Footer className="flex items-center justify-center text-foreground flex-col gap-2">
                  <Button
                    data-cy="main-content-buy-button"
                    variant="tertiary"
                    id="buyMainContent"
                    onClick={goToCheckout}
                    className={cn(
                      'h-12 w-full p-4 flex items-center space-x-2.5 text-base',
                      hasQuantityError && '!opacity-50',
                    )}
                    disabled={hasQuantityError || !canBuy}
                  >
                    <Icon.Dollar width={24} height={24} />
                    <span>{getRaffleCtaLabel(raffle.status, 'Comprar Agora')}</span>
                    <MustangArrow />
                  </Button>
                </BottomFixed.Footer>
              </BottomFixed>
            </div>

            {/* Desktop Bottom Fixed */}
            <div
              className={cn('hidden lg:block', !canBuy && !isUpcoming && 'hidden')}
              data-cy="main-content-desktop-purchase"
            >
              <BottomFixed hasFooter>
                {!isUpcoming && (
                  <>
                    {packOffers.length > 0 && (
                      <div className="mb-4">
                        <PromotionPacks
                          offers={packOffers}
                          ticketPrice={raffle.ticketPrice || 0}
                          onSelectPack={handleComboClick}
                        />
                      </div>
                    )}
                    <BottomFixed.Header className="text-foreground flex flex-row justify-between items-start gap-2.5 w-full">
                      {raffle.addTicketQuantityButtonValue &&
                        raffle.addTicketQuantityButtonValue.some((value) => value > 0) &&
                        raffle.addTicketQuantityButtonValue.map((value: number) => (
                          <div className="flex-1 relative" key={value}>
                            {raffle.addTicketQuantityButtonValue[2] === value && (
                              <Badge
                                icon="Fire"
                                className="w-fit p-0.5 absolute -top-2 left-1/2 -translate-x-1/2 shadow-xl z-10"
                                text="popular"
                                cnText="text-xxs font-medium"
                                iconProps={{ width: 12, height: 12 }}
                              />
                            )}
                            <Button
                              data-cy={`main-content-add-quantity-${value}-button`}
                              variant="secondary"
                              onClick={() => addQuantity(value)}
                              className="w-full h-12 text-xl rounded-sm border-2 border-foreground/10 bg-foreground/10 px-6 py-3.5"
                              disabled={!canBuy}
                            >
                              {`+${value}`}
                            </Button>
                          </div>
                        ))}
                    </BottomFixed.Header>

                    <BottomFixed.Body className="flex flex-col rounded-lg gap-2.5 overflow-visible">
                      <div className="flex flex-row items-center justify-between w-full">
                        <div className="flex flex-col items-start justify-start">
                          <span className="text-foreground text-xs uppercase">total</span>
                          <span
                            data-cy="main-content-total-value"
                            className="text-secondary uppercase font-semibold text-xl"
                          >
                            {formatCurrency(totalValue)}
                          </span>
                        </div>

                        <div className="relative z-10 pointer-events-auto">
                          <QuantitySelector
                            count={ticketQuantity}
                            onSetCount={handleChangeQuantity}
                            min={raffle.minTicketsPerPurchase}
                            max={raffle.maxTicketsPerPurchase}
                            incrementStep={raffle.addTicketButtonStepIncrement}
                            decrementStep={raffle.addTicketButtonStepDecrement}
                          />
                        </div>
                      </div>
                    </BottomFixed.Body>
                  </>
                )}

                <BottomFixed.Footer className="flex items-center justify-center text-foreground flex-col gap-2">
                  <Button
                    data-cy="main-content-checkout-button"
                    onClick={goToCheckout}
                    variant="tertiary"
                    className={cn(
                      ' h-12 w-full p-4 flex items-center space-x-2.5 text-base',
                      hasQuantityError && '!opacity-50',
                    )}
                    disabled={hasQuantityError || !canBuy}
                  >
                    <Icon.Dollar width={24} height={24} />
                    <span>{getRaffleCtaLabel(raffle.status, 'ir para pagamento')}</span>
                  </Button>
                </BottomFixed.Footer>
              </BottomFixed>
            </div>
          </div>
        </div>

        {shouldShowLastWinners ? (
          <LastWinnersCarousel
            data-cy="main-content-last-winners"
            {...customerWinners}
            data={winnersData}
          />
        ) : null}

        {sortedRaffles && (
          <RafflesList data-cy="main-content-raffles-list" raffles={sortedRaffles} />
        )}
        {finishedRaffles && finishedRaffles.length > 0 && (
          <FinishedResultsSection
            data-cy="main-content-finished-raffles-list"
            raffles={finishedRaffles}
          />
        )}
      </Container>
      {footer}
    </>
  );
}
